import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { KEYBOARD_SHORTCUTS, shortcutKeys } from "Components/RichTextEditor";

const KBD_STYLE = {
  display: "inline-block",
  minWidth: "22px",
  padding: "0 6px",
  border: "1px solid #c3cad5",
  borderBottomWidth: "2px",
  borderRadius: "4px",
  background: "#f7f8fa",
  fontFamily: "inherit",
  fontSize: "12px",
  lineHeight: "20px",
  textAlign: "center",
  whiteSpace: "nowrap",
};

// One binding as key caps: Ctrl + Shift + Z.
function Keys({ binding }) {
  return shortcutKeys(binding).map((key, index) => (
    <React.Fragment key={key}>
      {index > 0 && " + "}
      <kbd style={KBD_STYLE}>{key}</kbd>
    </React.Fragment>
  ));
}

function KeyboardShortcutsTable() {
  return (
    <Table size="small">
      <TableBody>
        {KEYBOARD_SHORTCUTS.map(({ group, items }) => (
          <React.Fragment key={group}>
            <TableRow>
              <TableCell
                colSpan={2}
                style={{ fontWeight: 600, background: "#f3f5f8" }}
              >
                {group}
              </TableCell>
            </TableRow>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.label}</TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>
                  {item.typed && (
                    <>
                      <code>{item.typed}</code> then{" "}
                    </>
                  )}
                  {item.keys.map((binding, index) => (
                    <React.Fragment key={binding}>
                      {index > 0 && " or "}
                      <Keys binding={binding} />
                    </React.Fragment>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * The `ordershortcuts` toolbar button: a reference list so drafters can find
 * the abbreviations, and the editor's keyboard shortcuts alongside them.
 *
 * Replaces registerShortcutsButton, which built the same table as an HTML
 * string for TinyMCE's windowManager. Here it is an ordinary MUI dialog, so
 * the escaping that _esc did by hand is React's problem instead.
 */
export default function ShortcutsButton({ getList }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const list = open ? getList() || [] : [];

  return (
    <>
      <Tooltip title="Typing and keyboard shortcuts">
        <Button
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen(true)}
        >
          Shortcuts
        </Button>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Order Drafting shortcuts</DialogTitle>
        <Tabs
          value={tab}
          onChange={(event, value) => setTab(value)}
          indicatorColor="primary"
          textColor="primary"
          style={{ padding: "0 24px" }}
        >
          <Tab label="Typing" style={{ textTransform: "none" }} />
          <Tab label="Keyboard" style={{ textTransform: "none" }} />
        </Tabs>
        <DialogContent dividers>
          {tab === 0 ? (
            <>
              <Typography variant="body2" style={{ marginBottom: "10px" }}>
                Type the abbreviation followed by a <b>space</b> to expand it.
                Matching is case-insensitive; press <b>Ctrl+Z</b> to undo an
                expansion.
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Expands to</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.length ? (
                    list.map((shortcut) => (
                      <TableRow key={shortcut.abbreviation}>
                        <TableCell style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          {shortcut.abbreviation}
                        </TableCell>
                        <TableCell>{shortcut.expansion}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2}>No shortcuts configured.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          ) : (
            <KeyboardShortcutsTable />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
